import React, { useEffect, useMemo, useState } from 'react';
import { Archive, CalendarDays, Eye, LayoutGrid, List, MapPin, Plus, Search, Trash2, X } from 'lucide-react';
import { GeneratedCampaign } from '../types';
import { useTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';
import { CAMPAIGN_INDUSTRY_LABELS, getCampaignIndustry, isRealEstateCampaign } from '../utils/campaignIndustry';

interface CampaignHistoryProps {
  savedCampaigns: GeneratedCampaign[];
  onSelectCampaign: (campaign: GeneratedCampaign) => void;
  onDeleteCampaign: (id: string) => void;
  onNewCampaign: () => void;
  canCreateCampaign: boolean;
  canDeleteCampaign: boolean;
}

export const CampaignHistory: React.FC<CampaignHistoryProps> = ({
  savedCampaigns,
  onSelectCampaign,
  onDeleteCampaign,
  onNewCampaign,
  canCreateCampaign,
  canDeleteCampaign,
}) => {
  const { t, tx } = useTranslation();
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    try { return localStorage.getItem('istihdaf_campaign_view') === 'list' ? 'list' : 'grid'; }
    catch { return 'grid'; }
  });
  const [pendingDelete, setPendingDelete] = useState<GeneratedCampaign | null>(null);
  const locale = language === 'ar' ? 'ar-EG' : 'en-GB';
  const statusLabels: Record<NonNullable<GeneratedCampaign['status']>, string> = {
    draft: 'مسودة', ready_for_review: 'جاهزة للمراجعة', approved: 'معتمدة', active: 'نشطة', completed: 'مكتملة',
  };
  const availableIndustries = useMemo(() => Array.from(new Set(savedCampaigns.map((campaign) => getCampaignIndustry(campaign.projectInfo)))), [savedCampaigns]);

  const filteredCampaigns = useMemo(() => savedCampaigns.filter((campaign) => {
    if (industryFilter !== 'all' && getCampaignIndustry(campaign.projectInfo) !== industryFilter) return false;
    if (statusFilter !== 'all' && (campaign.status || 'draft') !== statusFilter) return false;
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;
    return [campaign.projectInfo.projectName, campaign.projectInfo.region, campaign.projectInfo.developerName || '', CAMPAIGN_INDUSTRY_LABELS[getCampaignIndustry(campaign.projectInfo)]]
      .some((value) => value.toLowerCase().includes(query));
  }), [savedCampaigns, searchTerm, industryFilter, statusFilter]);

  useEffect(() => {
    try { localStorage.setItem('istihdaf_campaign_view', viewMode); } catch { /* device preference only */ }
  }, [viewMode]);

  useEffect(() => {
    if (!pendingDelete) return;
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && setPendingDelete(null);
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [pendingDelete]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    onDeleteCampaign(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <div className="space-y-5 animate-fadeIn">
      <section className="page-hero compact-page-hero">
        <div>
          <span className="eyebrow"><Archive className="w-3.5 h-3.5" /> {tx('مكتبة الحملات')}</span>
          <h2>{t('history.title')}</h2>
          <p>{tx('راجع حملاتك في كل المجالات، وقارن الاستراتيجيات، وواصل العمل من حيث توقفت.')}</p>
        </div>
        <div className="page-hero-actions">
          <span className="count-summary"><strong>{savedCampaigns.length}</strong><small>{tx('حملة محفوظة')}</small></span>
          {canCreateCampaign && <button onClick={onNewCampaign} className="btn btn-primary"><Plus className="w-4 h-4" /> {t('history.newCampaign')}</button>}
        </div>
      </section>

      <div className="filter-bar">
        <label className="filter-search">
          <Search className="w-4 h-4" />
          <input
            type="search"
            placeholder={t('history.searchPlaceholder')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          {searchTerm && <button onClick={() => setSearchTerm('')} aria-label={tx('مسح البحث')}><X className="w-4 h-4" /></button>}
        </label>
        <div className="campaign-filter-controls">
          <select className="filter-select" value={industryFilter} onChange={(event) => setIndustryFilter(event.target.value)} aria-label={tx('تصفية حسب المجال')}>
            <option value="all">{tx('كل المجالات')}</option>
            {availableIndustries.map((industry) => <option key={industry} value={industry}>{tx(CAMPAIGN_INDUSTRY_LABELS[industry])}</option>)}
          </select>
          <select className="filter-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label={tx('تصفية حسب الحالة')}>
            <option value="all">{tx('كل الحالات')}</option>
            {Object.entries(statusLabels).map(([status, label]) => <option key={status} value={status}>{tx(label)}</option>)}
          </select>
          <span className="filter-result-count">{filteredCampaigns.length} {tx('نتيجة')}</span>
          <div className="campaign-view-switch" role="group" aria-label={tx('طريقة العرض')}>
            <button type="button" onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'is-active' : ''} aria-label={tx('عرض البطاقات')}><LayoutGrid /></button>
            <button type="button" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'is-active' : ''} aria-label={tx('عرض القائمة')}><List /></button>
          </div>
        </div>
      </div>

      {filteredCampaigns.length === 0 ? (
        <div className="empty-state data-panel">
          <span className="empty-state-icon"><Archive /></span>
          <h3>{searchTerm ? t('history.noResults') : tx('لم تنشئ أي حملة بعد')}</h3>
          <p>{searchTerm ? t('history.noResultsDesc') : tx('ابدأ بإضافة حملتك الأولى ودع استهداف يبني لك استراتيجية التسويق.')}</p>
          {canCreateCampaign && <button onClick={onNewCampaign} className="btn btn-primary"><Plus className="w-4 h-4" /> {t('history.startNow')}</button>}
        </div>
      ) : (
        <div className={`project-library-grid is-${viewMode}`}>
          {filteredCampaigns.map((campaign) => {
            const isRealEstate = isRealEstateCampaign(campaign.projectInfo);
            const status = campaign.status || 'draft';
            return <article key={campaign.id} className="project-card">
              <div className="project-card-accent" />
              <header>
                <span className="project-region"><MapPin className="w-3.5 h-3.5" /> {tx(CAMPAIGN_INDUSTRY_LABELS[getCampaignIndustry(campaign.projectInfo)])} · {campaign.projectInfo.region}</span>
                <span className={`campaign-status-pill is-${status}`}>{tx(statusLabels[status])}</span>
              </header>
              <div className="project-card-body">
                <div className="project-card-identity"><span className="project-card-icon"><Archive className="w-5 h-5" /></span><div><h3>{campaign.projectInfo.projectName}</h3><p>{campaign.projectInfo.developerName || t('history.notSpecified')}</p><small className="project-date"><CalendarDays className="w-3.5 h-3.5" /> {new Date(campaign.createdAt).toLocaleDateString(locale)}</small></div></div>
                <dl>
                  <div><dt>{tx(isRealEstate ? 'السعر الأساسي' : 'سعر العرض')}</dt><dd dir="ltr">{campaign.projectInfo.startingPrice.toLocaleString(locale)} {campaign.projectInfo.currency}</dd></div>
                  <div><dt>{tx(isRealEstate ? 'خطة السداد' : 'مسار التحويل')}</dt><dd>{isRealEstate ? `${campaign.projectInfo.downPaymentPercent}% · ${campaign.projectInfo.installmentYears} ${t('history.years')}` : tx(campaign.projectInfo.conversionChannel || 'whatsapp')}</dd></div>
                  <div><dt>{tx(isRealEstate ? 'الوحدة' : 'العرض')}</dt><dd>{campaign.projectInfo.unitType || tx(isRealEstate ? 'عقار' : 'منتج أو خدمة')}</dd></div>
                </dl>
              </div>
              <footer>
                <button onClick={() => onSelectCampaign(campaign)} className="btn btn-secondary flex-1"><Eye className="w-4 h-4" /> {t('history.viewStrategy')}</button>
                {canDeleteCampaign && <button onClick={() => setPendingDelete(campaign)} className="icon-button danger" title={t('history.deleteCampaign')}><Trash2 className="w-4 h-4" /></button>}
              </footer>
            </article>;
          })}
        </div>
      )}

      {pendingDelete && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.currentTarget === event.target && setPendingDelete(null)}>
          <section className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-dialog-title" aria-describedby="delete-dialog-description">
            <span className="confirm-dialog-icon danger"><Trash2 className="w-5 h-5" /></span>
            <div>
              <h3 id="delete-dialog-title">{tx('حذف المشروع؟')}</h3>
              <p id="delete-dialog-description">{tx('سيتم حذف')} <strong>“{pendingDelete.projectInfo.projectName}”</strong> {tx('نهائيًا من مشاريعك المحفوظة. لا يمكن التراجع عن هذا الإجراء.')}</p>
            </div>
            <div className="confirm-dialog-actions">
              <button onClick={() => setPendingDelete(null)} className="btn btn-secondary">{tx('إلغاء')}</button>
              <button onClick={confirmDelete} className="btn btn-danger"><Trash2 className="w-4 h-4" /> {tx('حذف المشروع')}</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};
